import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { generateReportCardComments } from "@/lib/teacher-assistant.functions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, FileText, Printer } from "lucide-react";

export const Route = createFileRoute("/teacher/reports/cards")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Report Cards · MAKAI Teachers" }] }),
  component: Page,
});

type Learner = { id: string; first_name: string; last_name: string };

function Page() {
  const ctx = useTeacherContext();
  const gen = useServerFn(generateReportCardComments);
  const [streamId, setStreamId] = useState("");
  const [termId, setTermId] = useState<string | undefined>();
  const [terms, setTerms] = useState<{ id: string; name: string }[]>([]);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [selectedLearner, setSelectedLearner] = useState("");
  const [notes, setNotes] = useState("");
  const [tone, setTone] = useState("encouraging");
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState<any[]>([]);

  useEffect(() => {
    const sid = ctx.schoolId; if (!sid) return;
    (async () => {
      const { data: ts } = await supabase.from("terms").select("id, name").eq("school_id", sid).order("starts_on", { ascending: false });
      setTerms(ts ?? []);
    })();
  }, [ctx.schoolId]);

  useEffect(() => {
    if (!streamId) { setLearners([]); return; }
    (async () => {
      const { data } = await supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", streamId).eq("status", "active").order("first_name");
      setLearners(data ?? []);
    })();
  }, [streamId]);

  useEffect(() => {
    const sid = ctx.schoolId; if (!sid) return;
    (async () => {
      const { data } = await supabase.from("report_cards").select("id, learner_id, overall_comment, conduct_comment, head_teacher_comment, created_at, learner:learners(first_name, last_name)").eq("school_id", sid).order("created_at", { ascending: false }).limit(20);
      setCards(data ?? []);
    })();
  }, [ctx.schoolId, busy]);

  async function generate() {
    if (!selectedLearner) { toast.error("Pick a learner"); return; }
    setBusy(true);
    try {
      const className = ctx.streams.find((s) => s.id === streamId)?.class_name ?? "";
      await gen({ data: { learnerId: selectedLearner, termId, streamId, className, performanceNotes: notes, tone } });
      toast.success("Report comments saved");
      setNotes("");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <AppShell role="teacher" title="Report Card Generator" subtitle="AI-assisted, term-aware, parent-ready">
      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Generate comments</div>
          <div>
            <Label>Class / Stream</Label>
            <Select value={streamId} onValueChange={setStreamId}>
              <SelectTrigger><SelectValue placeholder="Choose stream" /></SelectTrigger>
              <SelectContent>{ctx.streams.map((s) => <SelectItem key={s.id} value={s.id}>{s.class_name} · {s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Term</Label>
            <Select value={termId ?? ""} onValueChange={(v) => setTermId(v || undefined)}>
              <SelectTrigger><SelectValue placeholder="Choose term" /></SelectTrigger>
              <SelectContent>{terms.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Learner</Label>
            <Select value={selectedLearner} onValueChange={setSelectedLearner}>
              <SelectTrigger><SelectValue placeholder="Choose learner" /></SelectTrigger>
              <SelectContent>{learners.map((l) => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="encouraging">Encouraging</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="firm but kind">Firm but kind</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Performance notes</Label>
            <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Strengths, areas to improve, attendance, key marks…" />
          </div>
          <Button onClick={generate} disabled={busy} className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate & save
          </Button>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4" /> Saved report cards</div>
            <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" /> Print</Button>
          </div>
          {cards.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No report cards generated yet.</div>
          ) : (
            <ul className="space-y-4">
              {cards.map((c: any) => (
                <li key={c.id} className="rounded-xl border border-border bg-background/60 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-display font-bold">{c.learner?.first_name} {c.learner?.last_name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-semibold text-primary">Subject teacher:</span> {c.overall_comment}</div>
                    {c.conduct_comment && <div><span className="font-semibold text-primary">Conduct:</span> {c.conduct_comment}</div>}
                    {c.head_teacher_comment && <div><span className="font-semibold text-primary">Head teacher:</span> {c.head_teacher_comment}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
